import { usePersistedState } from '../../lib/persist.js'
import NewPatientStat from './NewPatientStat.jsx'
import RevenueStat from './RevenueStat.jsx'
import UpsaleStat from './UpsaleStat.jsx'
import WhiteningRevenueStat from './WhiteningRevenueStat.jsx'

// Marketing campaigns we run ads for.
//   stat    = appointmentList.stat_type value (new-patient filter)
//   revType = revenue `type` filter for this campaign's treatment (omit = no revenue graphs)
//   upsale  = show the Upsale Performance sub-tab (Lock-in & ByeBye Braces)
// Verify the exact strings against your data (esp. Cool Light / Zoom + each revType).
const CAMPAIGNS = [
  { label: 'จัดฟัน', stat: 'ortho', revType: 'ortho', upsale: true },
  { label: 'ดี-aligner', stat: 'd-aligner', revType: 'd-aligner' },
  { label: 'Invisalign', stat: 'invisalign', revType: 'invisalign' },
  { label: 'Whitening', stat: 'whitening', revType: 'whitening', whitening: true },
  { label: 'Retainer', stat: 'retainer', revType: 'retainer' },
  { label: 'Implant', stat: 'implant', revType: 'implant' },
  { label: 'Veneer', stat: 'veneer', revType: 'veneer' },
  { label: 'ประกันสังคม', stat: 'sso', revType: 'sso' },
  { label: 'สปสช.', stat: 'nhso', revType: 'nhso' },
]

const pill = (on) =>
  `rounded-full px-3 py-1 text-[12.5px] border transition ${on ? 'border-brand-300 bg-brand-50 text-brand-700 font-medium' : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'}`

const subPill = (on) =>
  `rounded-lg px-4 py-1.5 text-[13px] font-medium border transition ${on ? 'bg-white text-slate-900 border-slate-300 shadow-sm' : 'bg-transparent text-slate-500 border-transparent hover:text-slate-700'}`

export default function CampaignStat({ excludeNhso }) {
  const [sel, setSel] = usePersistedState('stat:campaign', CAMPAIGNS[0].stat) // view one campaign at a time
  const [subTab, setSubTab] = usePersistedState('stat:campaign:subtab', 'patients') // patients | revenue | upsale
  // guard against a stale persisted sel (e.g. a campaign that was removed)
  const campaign = CAMPAIGNS.find((c) => c.stat === sel) || CAMPAIGNS[0]
  const active = [campaign]
  const hasSubTabs = campaign.revType || campaign.upsale
  // normalize sub-tab so an invalid persisted value falls back to patients
  const effSubTab = (subTab === 'revenue' && campaign.revType) || (subTab === 'upsale' && campaign.upsale) ? subTab : 'patients'

  // reset sub-tab to patients when switching to a campaign that lacks the current one
  const selectCampaign = (stat) => {
    setSel(stat)
    const c = CAMPAIGNS.find((x) => x.stat === stat)
    setSubTab((prev) => {
      if (prev === 'revenue' && c?.revType) return prev
      if (prev === 'upsale' && c?.upsale) return prev
      return 'patients'
    })
  }

  return (
    <>
      {/* campaign picker */}
      <div className="flex flex-wrap items-center gap-1.5 mb-4">
        <span className="text-[12.5px] text-slate-500 mr-1">{'เลือกแคมเปญ'}</span>
        {CAMPAIGNS.map((c) => (
          <button key={c.stat} onClick={() => selectCampaign(c.stat)} className={pill(campaign.stat === c.stat)}>{c.label}</button>
        ))}
      </div>

      {/* sub-tab: shown for campaigns with revenue and/or upsale data */}
      {hasSubTabs && (
        <div className="inline-flex items-center rounded-xl border border-slate-200 bg-slate-50 p-1 mb-5">
          <button onClick={() => setSubTab('patients')} className={subPill(effSubTab === 'patients')}>
            {'คนไข้นัดใหม่'}
          </button>
          {campaign.revType && (
            <button onClick={() => setSubTab('revenue')} className={subPill(effSubTab === 'revenue')}>
              {'รายได้'}
            </button>
          )}
          {campaign.upsale && (
            <button onClick={() => setSubTab('upsale')} className={subPill(effSubTab === 'upsale')}>
              {'Promotion'}
            </button>
          )}
        </div>
      )}

      {/* key={sel} forces remount on campaign change */}
      <div style={{ display: !hasSubTabs || effSubTab === 'patients' ? 'block' : 'none' }}>
        <NewPatientStat key={`np-${campaign.stat}`} excludeNhso={excludeNhso} campaigns={active} />
      </div>

      {campaign.revType && (
        <div style={{ display: effSubTab === 'revenue' ? 'block' : 'none' }}>
          {campaign.whitening
            ? <WhiteningRevenueStat key={`rev-${campaign.stat}`} />
            // excludeNhso={false}: the campaign tab always includes สปสช (see Statistic.jsx);
            // RevenueStat's default is true, which silently zeroed the สปสช. campaign
            : <RevenueStat key={`rev-${campaign.stat}`} revenueType={campaign.revType} excludeNhso={false} />}
        </div>
      )}

      {campaign.upsale && (
        <div style={{ display: effSubTab === 'upsale' ? 'block' : 'none' }}>
          <UpsaleStat key={`up-${campaign.stat}`} campaign={campaign} />
        </div>
      )}
    </>
  )
}
